import { useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button-simple';

type FacingMode = 'user' | 'environment';

type CameraCaptureProps = {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  className?: string;
};

function dataUrlToFile(dataUrl: string, fileName: string) {
  const [meta, data] = dataUrl.split(',');
  const mimeMatch = meta.match(/data:(.*?);/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binaryString = atob(data);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: mimeType });
}

export function CameraCapture({ open, onClose, onCapture, className }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>('environment');
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  const canCapture = !cameraError && cameraReady;

  useEffect(() => {
    if (!open) return undefined;

    let isMounted = true;

    async function openCamera() {
      try {
        setCameraError('');
        setCameraReady(false);
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });

        if (!isMounted) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          await videoRef.current.play();
          setCameraReady(true);
        }
      } catch {
        setCameraReady(false);
        setCameraError('Could not access camera. Please check browser permissions.');
      }
    }

    function closeCamera() {
      if (!streamRef.current) return;
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraReady(false);
    }

    openCamera();

    return () => {
      isMounted = false;
      closeCamera();
    };
  }, [open, facingMode]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className={cn('w-full max-w-2xl rounded-lg border border-border bg-background p-4 shadow-lg', className)}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">Take Photo</h3>
          <Button variant="ghost" className="h-8 px-2" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="overflow-hidden rounded-md border border-border/70 bg-muted/30">
          <video ref={videoRef} className="max-h-[60vh] w-full bg-black object-contain" playsInline muted />
        </div>

        {cameraError ? <p className="mt-2 text-sm text-red-600">{cameraError}</p> : null}

        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setFacingMode((current) => (current === 'environment' ? 'user' : 'environment'));
            }}
          >
            Switch Camera
          </Button>
          <Button
            disabled={!canCapture}
            onClick={() => {
              if (!videoRef.current || !canvasRef.current) return;
              const video = videoRef.current;
              const canvas = canvasRef.current;

              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;

              const context = canvas.getContext('2d');
              if (!context) return;

              context.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imageDataUrl = canvas.toDataURL('image/jpeg', 0.95);
              const imageFile = dataUrlToFile(imageDataUrl, `camera-${Date.now()}.jpg`);
              onCapture(imageFile);
              onClose();
            }}
          >
            Capture
          </Button>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
